import type { ReactNode } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { ShieldButton } from '@sorare/core/src/atoms/buttons/ShieldButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { ArenaCashBg } from '@sorare/core/src/atoms/icons/ArenaCashBg';
import { ArenaCashWordmark } from '@sorare/core/src/atoms/icons/ArenaCashWordmark';
import { ArenaEssenceBg } from '@sorare/core/src/atoms/icons/ArenaEssenceBg';
import { ArenaEssenceWordmark } from '@sorare/core/src/atoms/icons/ArenaEssenceWordmark';
import { Trophy } from '@sorare/core/src/atoms/icons/Trophy';
import { Centered } from '@sorare/core/src/atoms/layout/Centered';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL, BrandXXS, LabelS } from '@sorare/core/src/atoms/typography';
import { ShieldShape } from '@sorare/core/src/atoms/ui/ShieldShape';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { unitMapping } from '@sorare/core/src/lib/style';

import { PickerHeader } from '#src/components/pickers/PickerHeader/index.tsx';

const ProModeIcon = ({ size = 3 }: { size?: keyof typeof unitMapping }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 19 24"
    fill="none"
    style={{ height: unitMapping[size], width: 'auto' }}
  >
    <path
      d="M7.61276 0.233622L8.43518 1.24408L8.43423 1.32956L14.2976 9.15874L15.3584 8.50536L15.535 8.88524L18.4914 13.8473L18.5436 19.8902L14.402 22.2207L12.4552 15.5074L9.4371 14.0363L6.15405 15.2424L2.75798 14.0363V9.20338L0.0380859 8.21001V4.74462L2.75798 3.06653V2.98011L2.81021 3.03424L2.82256 3.02759L3.76179 3.99912V4.01526L4.63835 4.91936V1.71703L7.42282 0L7.43802 0.018044V0L7.61276 0.233622ZM3.73045 13.3487L6.14835 14.2082L9.10186 13.1237L9.49028 12.9803L13.2691 14.8227L14.9937 20.7715L17.5654 19.3242L17.5208 14.1189L14.9908 9.87291L14.8076 9.98687L14.0516 10.4522L7.45796 1.64865V1.58407L7.20724 1.27542L5.61083 2.2593V7.31921L2.78932 4.40938V4.39228L2.66586 4.26503L1.01056 5.28689V7.52909L3.73045 8.52341V13.3487Z"
      fill="currentColor"
    />
    <path
      d="M4.68004 9.5104L0 4.68221V15.7378L4.68004 21.7364L6.67929 15.7378L9.69743 17.2092L11.2442 24L15.7337 21.3969V15.5492L12.5647 10.2297V12.2292L4.68004 1.70183V9.5104Z"
      fill="currentColor"
    />
    <path
      d="M15.3585 8.55092L15.8595 9.39237L13.0598 11.0596L12.5587 10.2182L15.3585 8.55092Z"
      fill="currentColor"
    />
  </svg>
);

export const Mode = {
  PRO: 'PRO',
  ARENA_CASH: 'ARENA_CASH',
  ARENA_ESSENCE: 'ARENA_ESSENCE',
} as const;

export type Mode = (typeof Mode)[keyof typeof Mode];

export const modeColors: Record<Mode, string> = {
  [Mode.PRO]: 'var(--c-inventory-orange)',
  [Mode.ARENA_CASH]: 'var(--c-unique)',
  [Mode.ARENA_ESSENCE]: 'var(--c-arena-primary)',
};

const modeMessages = defineMessages({
  [Mode.PRO]: { defaultMessage: 'Pro' },
  [Mode.ARENA_CASH]: { defaultMessage: 'Arena Cash' },
  [Mode.ARENA_ESSENCE]: { defaultMessage: 'Arena Essence' },
});

type ModeIconProps = {
  mode: Mode;
  size?: keyof typeof unitMapping;
};

export const ModeIcon = ({ mode, size = 3 }: ModeIconProps) => {
  if (mode === Mode.PRO) return <ProModeIcon size={size} />;
  return <Trophy size={size} />;
};

const allModes = [Mode.PRO, Mode.ARENA_CASH, Mode.ARENA_ESSENCE] as const;

const CardsList = styled(Vertical)`
  padding: var(--unit) var(--double-unit) var(--double-unit);
`;

const CardBezel = styled(Bezel)`
  display: flex;
  cursor: pointer;

  &.full {
    width: 100%;
    align-items: flex-end;
    text-align: left;
  }

  &.arena {
    flex: 1;
    min-width: 0;
    align-items: center;
    justify-content: center;
  }
`;

const ModeCardContent = styled(Vertical).attrs({ gap: 1 })`
  padding: var(--double-unit);
  width: 100%;
  position: relative;
`;

const ArenaBgWrapper = styled(Centered)`
  position: absolute;
  inset: 0;
  pointer-events: none;
  color: var(--c-white);
  opacity: 0.1;
`;

const CompetitiveDivider = styled(Horizontal).attrs({ gap: 0, center: true })`
  padding: var(--unit) 0;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
`;

const DividerCenter = styled(Vertical).attrs({ gap: 1, center: true })`
  padding: 0 var(--unit);
`;

const ArenaRow = styled(Horizontal).attrs({ gap: 1 })`
  width: 100%;
`;

type SinglePlayerCardProps = {
  mode: Mode;
  selected: boolean;
  onClick: () => void;
};

const SinglePlayerCard = ({ mode, onClick }: SinglePlayerCardProps) => {
  const color = modeColors[mode];
  return (
    <CardBezel
      className="full"
      background="conic-gradient(from 90deg at 3px 3px, var(--c-nd-50) 25%, transparent 0) 0 0/5px 5px"
      borderColor="var(--c-nd-150)"
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      <ModeCardContent>
        <Horizontal>
          <ShieldShape
            size="sm"
            orientation="up"
            borderColor={color}
            icon={
              <span style={{ color: 'var(--c-white)' }}>
                <ModeIcon mode={mode} />
              </span>
            }
          />
          <BrandL color="var(--c-white)">
            <FormattedMessage {...modeMessages[mode]} />
          </BrandL>
        </Horizontal>
        <LabelS color="var(--c-nd-400)">
          <FormattedMessage defaultMessage="Build your legacy" />
        </LabelS>
      </ModeCardContent>
    </CardBezel>
  );
};

type ArenaCardProps = {
  mode: Mode;
  label: ReactNode;
  subtitle: ReactNode;
  onClick: () => void;
};

const ArenaCardComponent = ({
  mode,
  label,
  subtitle,
  onClick,
}: ArenaCardProps) => {
  const color = modeColors[mode];
  const bgIcon =
    mode === Mode.ARENA_CASH ? <ArenaCashBg /> : <ArenaEssenceBg />;
  return (
    <CardBezel
      className="arena"
      background="conic-gradient(from 90deg at 3px 3px, var(--c-nd-50) 25%, transparent 0) 0 0/5px 5px"
      borderColor={`linear-gradient(to bottom, var(--c-nd-150) 20%, ${color} 100%)`}
      boxShadow={`inset 0px -20px 20px -20px ${color}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      <ArenaBgWrapper>{bgIcon}</ArenaBgWrapper>
      <ModeCardContent
        style={{
          alignItems: 'center',
          textAlign: 'center',
          padding: 'var(--quadruple-unit) var(--triple-unit)',
        }}
      >
        {label}
        <LabelS color="var(--c-nd-600)" className="text-center" bold>
          {subtitle}
        </LabelS>
      </ModeCardContent>
    </CardBezel>
  );
};

type Props = {
  modes?: readonly Mode[];
  selected: Mode;
  onSelect: (mode: Mode) => void;
};

export const ModePicker = ({ modes = allModes, selected, onSelect }: Props) => {
  const { formatMessage } = useIntl();

  if (modes.length <= 1) return null;

  return (
    <Dropdown
      align="center"
      gap={8}
      label={props => (
        <ShieldButton
          type="button"
          aria-label={formatMessage(modeMessages[selected])}
          size="md"
          borderColor={modeColors[selected]}
          icon={
            <span style={{ color: 'var(--c-white)' }}>
              <ModeIcon mode={selected} />
            </span>
          }
          orientation="up"
          {...props}
        />
      )}
    >
      {({ closeDropdown }) => (
        <>
          <PickerHeader onClose={closeDropdown}>
            <FormattedMessage defaultMessage="CHOOSE MODE" />
          </PickerHeader>
          <CardsList>
            <SinglePlayerCard
              mode={Mode.PRO}
              selected={selected === Mode.PRO}
              onClick={() => {
                onSelect(Mode.PRO);
                closeDropdown();
              }}
            />

            <CompetitiveDivider>
              <DividerLine />
              <DividerCenter>
                <ShieldShape
                  size="xxs"
                  orientation="up"
                  borderColor={modeColors[Mode.ARENA_CASH]}
                  icon={
                    <span style={{ color: 'var(--c-white)' }}>
                      <ModeIcon mode={Mode.ARENA_CASH} size={1.5} />
                    </span>
                  }
                />
                <BrandXXS
                  color="var(--c-white)"
                  style={{ letterSpacing: '0.08em' }}
                >
                  <FormattedMessage defaultMessage="COMPETITIVE MODES" />
                </BrandXXS>
              </DividerCenter>
              <DividerLine />
            </CompetitiveDivider>

            <ArenaRow>
              <ArenaCardComponent
                mode={Mode.ARENA_CASH}
                label={<ArenaCashWordmark />}
                subtitle={<FormattedMessage defaultMessage="Cash" />}
                onClick={() => {
                  onSelect(Mode.ARENA_CASH);
                  closeDropdown();
                }}
              />
              <ArenaCardComponent
                mode={Mode.ARENA_ESSENCE}
                label={<ArenaEssenceWordmark />}
                subtitle={<FormattedMessage defaultMessage="Essence" />}
                onClick={() => {
                  onSelect(Mode.ARENA_ESSENCE);
                  closeDropdown();
                }}
              />
            </ArenaRow>
          </CardsList>
        </>
      )}
    </Dropdown>
  );
};
